import { useEffect } from "react";
import { useFeatureFlagEnabled, usePostHog } from "@posthog/react";

/* Consts */
const DEFAULT_STALE_MS = 5 * 60 * 1000;

let lastReloadAt = 0;
let isReloading = false;

/**
 * Reloads feature flags if the cache is stale.
 *
 * @description This function checks if the feature flags cache is stale based on the provided `staleMs` duration.
 * If the cache is stale and a reload is not already in progress, it triggers a reload of the feature flags
 * using the provided `reloadFeatureFlags` function.
 *
 * @param reloadFeatureFlags - A function that triggers the reload of feature flags.
 * @param staleMs - The duration in milliseconds after which the cache is considered stale. Defaults to 5 minutes.
 */
const reloadFlagsIfStale = (reloadFeatureFlags: () => void, staleMs: number) => {
  const now = Date.now();
  // Check if the cache is stale
  const isStale = now - lastReloadAt >= staleMs;

  // If the cache is not stale or a reload is already in progress, do nothing
  if (!isStale || isReloading) {
    return;
  }

  // Mark as reloading and update the last reload time
  isReloading = true;
  lastReloadAt = now;

  // Try to reload feature flags and ensure that the reloading state is reset afterwards
  try {
    reloadFeatureFlags();
  } finally {
    isReloading = false;
  }
};

/**
 * Wrapper around PostHog's useFeatureFlagEnabled that refreshes feature flags
 * when the cache is stale.
 */
export const useFeatureFlag = (flagKey: string, staleMs = DEFAULT_STALE_MS) => {
  const posthog = usePostHog();

  // Reload feature flags if the cache is stale on component mount and whenever PostHog instance changes
  useEffect(() => {
    if (!posthog?.reloadFeatureFlags) {
      return;
    }

    reloadFlagsIfStale(() => posthog.reloadFeatureFlags(), staleMs);
  }, [posthog, staleMs]);

  // Return the feature flag value using PostHog's hook
  return useFeatureFlagEnabled(flagKey);
};
